// The instruction sent to the agent when "Generate with AI" is clicked on the Commit stage's Methodology
// section, plus the label the UI would show in its place if this turn ever surfaced in a visible
// transcript. Same shape and convention as ./greetingPrompt and ./autoMapPrompt: pure, standalone (so the
// wording can be asserted in a test with no hooks/WS involved), and deliberately short — the general
// formatting rules already in `app/agent/prompts/system.py` are what makes the agent's reply come out as
// headings + bullet lists + an optional reviewer note; this only says what the situation adds.
//
// Sent over a DEDICATED WebSocket connection (see ../api/generateMethodologyViaChat.js), not the one
// backing the visible ChatPanel — so asking for a methodology from the Commit stage does not interrupt or
// appear inside whatever the user is chatting about elsewhere on Working data. It is still the SAME agent,
// reading the SAME conversation history (the backend keys chat history by session id, not by connection),
// so the reply reflects everything that has actually happened in this session. The turn is sent
// `ephemeral`, so it is never saved: it does not appear in the chat later and never joins that history.

/**
 * @param {object} args
 * @param {string} [args.target]  AiIngestTarget name, when the session carries one.
 * @returns {{text: string, display: string}} `text` goes to the model; `display` marks the request as
 *   app-initiated server-side (which exempts it from the lock-wait notice). The turn is never rendered or
 *   saved, so the label is not shown anywhere.
 */
export function buildGenerateMethodologyPrompt({ target = "" } = {}) {
  const destination = target ? `the ${target} data task` : "the destination data task";

  return {
    display: "Writing a methodology statement…",
    text: [
      `Write a methodology statement for ${destination}, describing how this data was collected and`,
      "prepared.",
      "",
      "Describe the CURRENT state of the data as it will be committed — not a turn-by-turn account of",
      "every back-and-forth change that led here. If an earlier step was later reversed or superseded,",
      "describe the net result only, not the history.",
      "",
      "This is going into the task's own Methodology field, for a reviewer checking the numbers — not for",
      "me. End with a short note for reviewers if there is anything genuinely worth flagging (a mismatched",
      "reporting period, rows dropped, values converted or estimated). Omit it if there is nothing to flag.",
      "",
      "Reply with the methodology statement only — no preamble, no questions.",
    ].join("\n"),
  };
}

export default buildGenerateMethodologyPrompt;
